import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  ChevronDown,
  Target,
  ShieldCheck,
  CheckCircle2,
  Users2,
  Zap,
  TrendingUp,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import {
  FUNNEL_STAGES,
  LEAD_GEN_SERVICES,
  ADVANCED_TECHNIQUES,
  LEAD_GEN_FAQS,
} from "../../Data/leadGenerationData";

// Reusing existing shared stylesheets to eliminate duplication
import "../../Style/services/data-validation.css";
import "../../Style/services/b2b-appointment.css";
import "../../Style/services/lead-generation.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -6, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function LeadGeneration() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [activeFunnelStage, setActiveFunnelStage] = useState(0);

  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleFaq = (id) => {
    setActiveFaq((prev) => (prev === id ? null : id));
  };

  return (
    <div className="validation-page leadgen-page">
      <Seo
        title="Multichannel B2B Lead Generation Agency | Conectify Tech"
        description="A multifaceted approach to B2B lead generation. Proven methods, multi-tier data qualification, demand generation, and appointment scheduling."
      />

      {/* 1. Hero Section */}
      <section className="val-hero leadgen-hero">
        <Container className="val-hero__container">
          <motion.div
            className="val-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>Multichannel Sales Engine</span>
            </div>

            <h1 className="val-hero__title">
              Leading Company in the{" "}
              <span className="text-gradient">Multichannel Lead Generation</span>
            </h1>

            <p className="val-hero__lead">
              A multifaceted approach to lead generation increases your company's ability to reach
              new levels in your industry. As a lead generation agency, Conectify Tech’s tailored
              campaigns offer you first-class sales structures to drive your business forward. Our B2B
              lead generation solutions include proven methods to help you increase your sales and
              scale faster.
            </p>

            <p className="val-hero__sublead">
              Conectify Tech is the partner you need to improve your B2B sales strategy. Our tried
              and tested approach is based on the premise that reliability, transparency and
              communication are the cornerstones of successful partnerships. Together, our team,
              proprietary technology, and processes will grow your business. Find out how this expert
              lead generation agency can transform your business.
            </p>

            <div className="val-hero__tags">
              <span className="val-tag">
                <Target size={13} className="text-primary" />
                <span>Demand Generation</span>
              </span>
              <span className="val-tag">
                <ShieldCheck size={13} className="text-primary" />
                <span>MQL & SQL Qualification</span>
              </span>
              <span className="val-tag">
                <CheckCircle2 size={13} className="text-success" />
                <span>Automated Lead Flow</span>
              </span>
            </div>

            <div className="val-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Scale Your Pipeline Now
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                Explore Capabilities
              </Button>
            </div>
          </motion.div>

          {/* Multi-Stage Conversion Pipeline HUD */}
          <motion.div
            className="val-hero__card-wrap"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="leadgen-funnel-board">
              <div className="leadgen-funnel-board__header">
                <div className="flex items-center gap-2">
                  <Zap size={18} className="text-primary" />
                  <span className="font-bold text-sm">Multichannel Sales Pipeline</span>
                </div>
                <span className="hud-live-tag">
                  <span className="live-dot"></span> Pipeline Active
                </span>
              </div>

              {/* Dynamic Funnel Layers */}
              <div className="funnel-layers-stack">
                {FUNNEL_STAGES.map((stage, idx) => {
                  const isSelected = activeFunnelStage === idx;
                  return (
                    <button
                      key={stage.step}
                      type="button"
                      className={`funnel-layer-bar funnel-layer--${idx + 1} ${isSelected ? "is-active" : ""}`}
                      onClick={() => setActiveFunnelStage(idx)}
                    >
                      <span className="funnel-layer-bar__num">{stage.step}</span>
                      <span className="funnel-layer-bar__title">{stage.title}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active Stage Callout (with proper spacing and badge tag) */}
              <div className="funnel-active-detail">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="stage-badge">Phase {FUNNEL_STAGES[activeFunnelStage].step}</span>
                  <span className="stage-divider">•</span>
                  <span className="stage-name">{FUNNEL_STAGES[activeFunnelStage].title}</span>
                </div>
                <p>{FUNNEL_STAGES[activeFunnelStage].desc}</p>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* 2. Lead Generation Services Include: 12 Tactical Chips */}
      <section className="leadgen-services-section">
        <Container>
          <div className="leadgen-services-card">
            <div className="section-eyebrow">Comprehensive Capabilities</div>
            <h2 className="section-title">Our Lead Generation Services Include:</h2>
            <div className="leadgen-chips-grid">
              {LEAD_GEN_SERVICES.map((srv) => {
                const Icon = srv.icon;
                return (
                  <motion.div
                    key={srv.label}
                    className="leadgen-chip-item"
                    whileHover={{ y: -3, scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="leadgen-chip-item__icon">
                      <Icon size={16} />
                    </div>
                    <span>{srv.label}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* 3. Advanced Techniques Section */}
      <section className="leadgen-techniques-section">
        <Container>
          <div className="appt-split-box">
            <div className="appt-split-box__content">
              <div className="section-eyebrow">Strategic Precision</div>
              <h2>Conectify Tech Advanced Techniques</h2>
              <p className="lead-text">
                While your focus is on your business, our focus is on you. Our mission is to make
                your company stand out in your industry. With our multi-channel lead generation
                approach, you will understand how a successful partnership with a B2B lead generation
                agency will boost your business.
              </p>
              <p className="lead-text">
                Conectify Tech’s innovative lead generation solutions fill the gaps in your current
                B2B sales campaign. In collaboration with your in-house sales team, Conectify Tech
                develops a personalized sales strategy that focuses on our multi-channel solutions
                such as B2B lead generation, demand generation, and appointment scheduling. Simply put,
                the benefits of Conectify Tech and proven lead generation strategies give you an
                accelerated opportunity.
              </p>
            </div>

            {/* 3 Core Synergy Cards */}
            <div className="leadgen-techniques-grid">
              {ADVANCED_TECHNIQUES.map((tech) => {
                const Icon = tech.icon;
                return (
                  <motion.div
                    key={tech.title}
                    className="leadgen-tech-card"
                    variants={cardHover}
                    initial="initial"
                    whileHover="hover"
                  >
                    <div className="leadgen-tech-card__icon">
                      <Icon size={20} />
                    </div>
                    <h4>{tech.title}</h4>
                    <p>{tech.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* 4. B2B Lead Generation Experts Narrative (Balanced 2-Column Layout) */}
      <section className="leadgen-experts-section">
        <Container>
          <div className="leadgen-experts-card">
            <div className="leadgen-experts-grid">
              {/* Left Column: Heading, Lead & Telemetry Badges */}
              <div className="leadgen-experts-intro">
                <div className="section-eyebrow">
                  <Users2 size={14} className="text-primary" />
                  <span>Proven Industry Authority</span>
                </div>

                <h2 className="leadgen-experts-title">
                  We are the <span className="text-gradient">B2B Lead Generation</span> Experts
                </h2>

                <p className="leadgen-experts-lead">
                  As you focus on your core business, we empower your revenue trajectory by aligning
                  data intelligence with high-converting sales pipelines.
                </p>

                {/* Authority Telemetry Stats */}
                <div className="leadgen-experts-stats">
                  <div className="expert-stat-box">
                    <span className="expert-stat-box__val text-primary">High-Yield</span>
                    <span className="expert-stat-box__lbl">Conversion Rate by Intent Data</span>
                  </div>
                  <div className="expert-stat-box">
                    <span className="expert-stat-box__val text-success">100%</span>
                    <span className="expert-stat-box__lbl">Criteria-Matched Client Profiles</span>
                  </div>
                </div>
              </div>

              {/* Right Column: 3 Structured Narrative Cards with Exact Copy */}
              <div className="leadgen-experts-cards">
                <div className="expert-narrative-card">
                  <div className="expert-narrative-card__header">
                    <div className="narrative-icon">
                      <Target size={18} />
                    </div>
                    <h4>Targeted ICP Identification</h4>
                  </div>
                  <p>
                    As you focus on your business, we will help you improve your focus by giving you
                    the opportunity to achieve your sales goals. Our approach to B2B lead generation
                    focuses on generating high-quality leads that convert at a higher rate by using
                    data to identify your ideal customer. Businesses will spend a large portion of
                    their budget completing sales efforts without understanding the importance of lead
                    generation to a successful B2B sales strategy.
                  </p>
                </div>

                <div className="expert-narrative-card">
                  <div className="expert-narrative-card__header">
                    <div className="narrative-icon">
                      <TrendingUp size={18} />
                    </div>
                    <h4>Multichannel Pipeline Acceleration</h4>
                  </div>
                  <p>
                    Don't waste resources giving up effective B2B lead generation strategies.
                    Instead, implement lead generation services that form successful B2B partnerships
                    with experienced Conectify Tech specialists. With our multi-channel approach to lead
                    generation, our B2B lead generation experts accelerate your business. Our team will
                    expand your current sales campaign to find qualified clients that meet your
                    defined criteria.
                  </p>
                </div>

                <div className="expert-narrative-card">
                  <div className="expert-narrative-card__header">
                    <div className="narrative-icon">
                      <ShieldCheck size={18} />
                    </div>
                    <h4>Integrated Sales Synergy</h4>
                  </div>
                  <p>
                    We work closely with your in-house sales team to develop bespoke sales strategies
                    using our proven solutions including B2B business lead generation, demand
                    generation, and appointment scheduling. Trust our lead generation specialists to
                    give your company a chance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* 5. Frequently Asked Questions Accordion */}
      <section className="leadgen-faq-section">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Knowledge Center</span>
            <h2 className="section-title">
              FREQUENTLY ASKED QUESTIONS ABOUT LEAD GENERATION SERVICES
            </h2>
            <p className="section-desc max-w-2xl mx-auto">
              Everything you need to know about our multichannel methodologies, qualifications, and
              execution protocols.
            </p>
          </div>

          <div className="leadgen-faq-list">
            {LEAD_GEN_FAQS.map((faq) => {
              const isOpen = activeFaq === faq.id;
              return (
                <div key={faq.id} className={`leadgen-faq-item ${isOpen ? "is-open" : ""}`}>
                  <button
                    type="button"
                    className="leadgen-faq-question"
                    onClick={() => toggleFaq(faq.id)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={18} className="faq-chevron" />
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        className="leadgen-faq-answer"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <p>{faq.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* 6. Bottom CTA */}
      <section className="b2b-cta">
        <Container>
          <div className="b2b-cta__card">
            <h2>Accelerate Your Business with Multichannel Lead Generation</h2>
            <p>
              Stop wasting budget on ineffective sales cycles. Partner with Conectify Tech to build a
              predictable, data-driven revenue engine.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Speak to a Lead Generation Specialist
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}